
//#region State Pattern

import { BehaviorSubject, Observable } from "rxjs";
import { StoryStates } from "./constants.lib";

/*
[
    {
        "id": 1,
        "user_story_status_description": "accepted"
    },
    {
        "id": 2,
        "user_story_status_description": "delivered"
    },
    {
        "id": 3,
        "user_story_status_description": "finished"
    },
    {
        "id": 4,
        "user_story_status_description": "rejected"
    },
    {
        "id": 5,
        "user_story_status_description": "started"
    },
    {
        "id": 6,
        "user_story_status_description": "unscheduled"
    },
    {
        "id": 7,
        "user_story_status_description": "unstarted"
    },
    {
        "id": 8,
        "user_story_status_description": "Deleted"
    }
]
*/

export type StoryStateType = { id: number, user_story_status_description: string };
export const StoryStatesObj: Record<StoryStates, StoryStateType> = {
  [StoryStates.Accepted]: {
    id: 1,
    user_story_status_description: "accepted"
  },
  [StoryStates.Delivered]: {
    id: 2,
    user_story_status_description: "delivered"
  },
  [StoryStates.Finished]: {
    id: 3,
    user_story_status_description: "finished"
  },
  [StoryStates.Rejected]: {
    id: 4,
    user_story_status_description: "rejected"
  },
  [StoryStates.Started]: {
    id: 5,
    user_story_status_description: "started"
  },
  [StoryStates.Unscheduled]: {
    id: 6,
    user_story_status_description: "unscheduled"
  },
  [StoryStates.Unstarted]: {
    id: 7,
    user_story_status_description: "unstarted"
  },
  [StoryStates.Deleted]: {
    id: 8,
    user_story_status_description: "Deleted"
  }
}

abstract class StatusState {
  protected displayState: StoryStateType;
  protected context?: StatusContext;
  private _nextState = new BehaviorSubject<StoryStateType | undefined>(undefined);
  protected get nextState() {
    return this._nextState.getValue();
  }
  protected set nextState(val: StoryStateType | undefined) {
    this._nextState.next(val);
  }
  protected get $nextState() {
    return this._nextState.asObservable();
  }

  constructor(storyState: StoryStateType, nextState?: StoryStateType) {
    this.displayState = storyState;
    this.nextState = nextState;
  }

  public setContext(context: StatusContext) {
    this.context = context;
  }

  public abstract changeHandler(): void;

  get status(): StoryStateType {
    return this.displayState;
  }
  get display(): StoryStateType | undefined {
    if (this.nextState)
      return this.nextState;
    return this.displayState;
  }
  get $display() {
    return this.$nextState;
  }
}

abstract class Creator {
  public abstract factoryMethod(state?: StoryStateType): StatusContext;
}

abstract class CreatorOnId {
  public abstract factoryMethod(id?: number): StatusContext;
}

export class ConcreteCreator extends Creator {
  public factoryMethod(state: StoryStateType = StoryStatesObj[StoryStates.Unscheduled]): StatusContext {
    switch (state.user_story_status_description.toLowerCase()) {
      case StoryStatesObj[StoryStates.Accepted].user_story_status_description.toLowerCase():
        return new StatusContext(new ConcreteStatusAccepted());
      case StoryStatesObj[StoryStates.Deleted].user_story_status_description.toLowerCase():
        return new StatusContext(new ConcreteStatusDeleted());
      case StoryStatesObj[StoryStates.Delivered].user_story_status_description.toLowerCase():
        return new StatusContext(new ConcreteStatusDelivered());
      case StoryStatesObj[StoryStates.Finished].user_story_status_description.toLowerCase():
        return new StatusContext(new ConcreteStatusFinished());
      case StoryStatesObj[StoryStates.Rejected].user_story_status_description.toLowerCase():
        return new StatusContext(new ConcreteStatusRejected());
      case StoryStatesObj[StoryStates.Started].user_story_status_description.toLowerCase():
        return new StatusContext(new ConcreteStatusStarted());
      case StoryStatesObj[StoryStates.Unstarted].user_story_status_description.toLowerCase():
        return new StatusContext(new ConcreteStatusUnstarted());
      default:
        return new StatusContext(new ConcreteStatusUnschedule());
    }
  }
}

export class ConcreteCreatorOnId extends CreatorOnId {
  public factoryMethod(id: number = StoryStatesObj[StoryStates.Unscheduled].id): StatusContext {
    switch (id) {
      case StoryStatesObj[StoryStates.Accepted].id:
        return new StatusContext(new ConcreteStatusAccepted());
      case StoryStatesObj[StoryStates.Deleted].id:
        return new StatusContext(new ConcreteStatusDeleted());
      case StoryStatesObj[StoryStates.Delivered].id:
        return new StatusContext(new ConcreteStatusDelivered());
      case StoryStatesObj[StoryStates.Finished].id:
        return new StatusContext(new ConcreteStatusFinished());
      case StoryStatesObj[StoryStates.Rejected].id:
        return new StatusContext(new ConcreteStatusRejected());
      case StoryStatesObj[StoryStates.Started].id:
        return new StatusContext(new ConcreteStatusStarted());
      case StoryStatesObj[StoryStates.Unstarted].id:
        return new StatusContext(new ConcreteStatusUnstarted());
      default:
        return new StatusContext(new ConcreteStatusUnschedule());
    }
  }
}

export class StatusContext {
  /**
   * @type {State} A reference to the current state of the Context.
   */
  private state: StatusState;

  constructor(
    state: StatusState
  ) {
    this.state = state;
    this.transitionTo(state);
  }

  /**
   * The Context allows changing the State object at runtime.
   */
  public transitionTo(state: StatusState): void {
    // console.log(`StatusContext: Trasition to ${(<any>state).constructor.name}.`);
    this.state = state;
    this.state.setContext(this);
  }

  /**
   * the Context delegates part of its behaviour to the current State object.
   */
  public requestChange(): void {
    this.state.changeHandler();
  }

  get status() {
    return this.state.status;
  }
  get display() {
    return this.state.display;
  }
  get $display() {
    return this.state.$display;
  }
}

//#region Concretes

class ConcreteStatusAccepted extends StatusState {
  constructor() {
    super(StoryStatesObj[StoryStates.Accepted]);
  }
  public override changeHandler(): void {
    // console.log('ConcreteStatusAccepted:changeHandler:', this.status); // JSON.stringify({ currentState: this.currentState }));
    // this.context.transitionTo(new ConcreteStatusAccepted());
  }
}

class ConcreteStatusDelivered extends StatusState {
  constructor() {
    super(StoryStatesObj[StoryStates.Delivered], StoryStatesObj[StoryStates.Accepted]);
  }
  public override changeHandler(): void {
    // console.log('ConcreteStatusDelivered:changeHandler:', this.status); // JSON.stringify({ currentState: this.currentState }));
    // console.log('ConcreteStatusDelivered wants to change the state of the context.', this.display);
    this.context?.transitionTo(new ConcreteStatusAccepted());
  }
}

class ConcreteStatusFinished extends StatusState {
  constructor() {
    super(StoryStatesObj[StoryStates.Finished], StoryStatesObj[StoryStates.Delivered]);
  }
  public override changeHandler(): void {
    // console.log('ConcreteStatusFinished:changeHandler:', this.status); // JSON.stringify({ currentState: this.currentState }));
    // console.log('ConcreteStatusFinished wants to change the state of the context.', this.display);
    this.context?.transitionTo(new ConcreteStatusDelivered());
  }
}

class ConcreteStatusRejected extends StatusState {
  constructor() {
    super(StoryStatesObj[StoryStates.Rejected], StoryStatesObj[StoryStates.Started]);
  }
  public override changeHandler(): void {
    // console.log('ConcreteStatusStarted:changeHandler:', this.status); // JSON.stringify({ currentState: this.currentState }));
    // console.log('ConcreteStatusStarted wants to change the state of the context.', this.display);
    this.context?.transitionTo(new ConcreteStatusStarted());
  }
}

class ConcreteStatusStarted extends StatusState {
  constructor() {
    super(StoryStatesObj[StoryStates.Started], StoryStatesObj[StoryStates.Finished]);
  }
  public override changeHandler(): void {
    // console.log('ConcreteStatusStarted:changeHandler:', this.status); // JSON.stringify({ currentState: this.currentState }));
    // console.log('ConcreteStatusStarted wants to change the state of the context.', this.display);
    this.context?.transitionTo(new ConcreteStatusFinished());
  }
}

class ConcreteStatusUnschedule extends StatusState {
  constructor() {
    super(StoryStatesObj[StoryStates.Unscheduled], StoryStatesObj[StoryStates.Unstarted]);
  }
  public override changeHandler(): void {
    // console.log('ConcreteStatusUnschedule:changeHandler:', this.status);
    // console.log('ConcreteStatusUnschedule wants to change the state of the context.', this.display);
    this.context?.transitionTo(new ConcreteStatusUnstarted());
  }
}

class ConcreteStatusUnstarted extends StatusState {
  constructor() {
    super(StoryStatesObj[StoryStates.Unstarted], StoryStatesObj[StoryStates.Started]);
  }
  public override changeHandler(): void {
    // console.log('ConcreteStatusUnschedule:changeHandler:', this.status);
    // console.log('ConcreteStatusUnschedule wants to change the state of the context.', this.display);
    this.context?.transitionTo(new ConcreteStatusStarted());
  }
}

class ConcreteStatusDeleted extends StatusState {
  constructor() {
    super(StoryStatesObj[StoryStates.Deleted]);
  }
  public override changeHandler(): void {
    // console.log('ConcreteStatusDeleted:changeHandler:', this.status); // JSON.stringify({ currentState: this.currentState }));
  }
}

//#endregion

//#endregion
